<template>
  <v-card
    elevation="0"
    tile
  >
    <v-card-title class="tab-title d-flex align-center">
      <v-btn
        v-if="showProviderProfileNavBtn"
        @click="emit('toggleProviderProfileNav')"
        :aria-label="$bct('Toggle provider profile navigation')"
        data-cy="provider_profile_nav_icon"
        icon="menu_open"
        variant="text"
      />
      <h2 class="v-card-title py-0 px-0 d-flex align-center">
        {{ $bct(title) }}
      </h2>

      <v-spacer />

      <StandardButton
        @click="$refs.newDialog.open()"
        id="manager_add_photo"
        role="create"
        text="Upload"
        tracked
      />
    </v-card-title>
    <v-divider />
    <v-card-text class="py-4">
      <v-row>
        <v-col>
          {{ $bct(description) }}
        </v-col>
      </v-row>
    </v-card-text>

    <ResourceDialog
      ref="newDialog"
      save-button-text="Done"
      title="Upload"
      close-on-save
    >
      <template #form>
        <dashboard
          :props="uppyProps"
          :uppy="uppy"
        />
      </template>
    </ResourceDialog>
  </v-card>
</template>

<script setup>
import { Dashboard } from '@uppy/vue';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';

import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useUppyUtils from '@/shared/composables/useUppyUtils';

const SUPPORTED_VIDEO_TYPES = ['avi', 'mov', 'mp4', 'mpeg', 'ogv', 'webm'];
const SUPPORTED_IMAGE_TYPES = ['heic', 'heif', 'jpg', 'jpeg', 'png', 'svg', 'tif', 'tiff', 'webp'];

const { api } = useApi();
const { getUploadAPIEndpoint, createUppyPropsWithLocale } = useUppyUtils();

const $bct = inject('translator');

const props = defineProps({
  category: {
    type: String,
    default: 'image',
  },
  description: {
    type: String,
    default: 'Make your profile stand out! Add photos of your classrooms and/or facility.',
  },
  providerId: {
    type: String,
    default: null,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
  title: {
    type: String,
    default: 'Photos',
  },
});

const emit = defineEmits(['load', 'toggleProviderProfileNav']);

const imageErrorMessage = computed(() => {
  return `This is an unsupported file type. Please only include the following image formats: ${SUPPORTED_IMAGE_TYPES.join(', ')}.`;
});
const videoErrorMessage = computed(() => {
  return `This is an unsupported file type. Please only include the following video formats: ${SUPPORTED_VIDEO_TYPES.join(', ')}.`;
});

const newDialog = ref(null);
const uppy = ref(null);
const { uppyProps, setupUppyWithTranslations } = createUppyPropsWithLocale();

onBeforeUnmount(() => {
  uppy.value?.close();
});

function load() {
  setupUppyWithTranslations(uppyInit);
}

function uppyInit() {
  uppy.value = new Uppy().use(XHRUpload, {
    endpoint: getUploadAPIEndpoint(),
  });

  uppy.value.on('file-added', (file) => {
    //  File type values might depend on browser or OS;
    //  without a check for just the extension, correct file type could be accidentally rejected
    const fileFormatted = file.name.split('.').pop().toLowerCase();

    if (props.category === 'image' && !SUPPORTED_IMAGE_TYPES.includes(fileFormatted)) {
      // eslint-disable-next-line no-alert
      alert($bct(imageErrorMessage.value));
      uppy.value.removeFile(file.id);
    } else if (props.category === 'video' && !SUPPORTED_VIDEO_TYPES.includes(fileFormatted)) {
      // eslint-disable-next-line no-alert
      alert($bct(videoErrorMessage.value));
      uppy.value.removeFile(file.id);
    }
  });

  uppy.value.on('upload-success', async (file, evt) => {
    const asset = {
      file: {
        id: evt.body.id, // remove the Shrine storage prefix
        storage: 'cache',
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      },
      category: props.category,
    };

    const response = await api.manager.provider.asset.create(props.providerId, asset);
    if (response?.status !== 201) return;

    newDialog.value.close();
    emit('load');
  });
}

onMounted(load);
</script>
